import { useAuth } from "@/hooks/useAuth";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { defaultFormatters } from "@/lib/utils";
import {
    NewMultiStepBitsGoalDoc,
    NewMultiStepGoalDoc,
    NewMultiStepGoalStep,
    NewMultiStepSubsGoalDoc,
} from "@ailenax-overlay/shared";
import { FC } from "react";
import { FaRegClipboard } from "react-icons/fa";

interface MultiStepGoalConfigCardProps {
    goal: NewMultiStepGoalDoc;
    setField: <G extends NewMultiStepGoalDoc, F extends keyof G>(field: F, value: G[F]) => void;
    setSteps: (steps: NewMultiStepGoalStep[]) => void;
    doSave: () => void;
    reset: () => void;
}

export const MultiStepGoalConfigCard: FC<MultiStepGoalConfigCardProps> = ({
    goal,
    setField,
    setSteps,
    doSave,
    reset,
}) => {
    const { token } = useAuth();
    const [, copyUrl] = useCopyToClipboard();

    const updateStep = (index: number, field: keyof NewMultiStepGoalStep, value: string | number) => {
        const next = goal.steps.map((step, i) =>
            i === index ? { ...step, [field]: value } : step
        );
        setSteps(next);
    };

    const addStep = () => {
        const lastGoal = goal.steps.at(-1)?.goal ?? 0;
        setSteps([...goal.steps, { title: `Step ${goal.steps.length + 1}`, goal: lastGoal + 100 }]);
    };

    const removeStep = (index: number) => {
        if (goal.steps.length <= 1) return;
        setSteps(goal.steps.filter((_, i) => i !== index));
    };

    const moveStep = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= goal.steps.length) return;
        const next = [...goal.steps];
        [next[index], next[target]] = [next[target], next[index]];
        setSteps(next);
    };

    return (
        <div className="col-span-12">
            <form className="flex flex-col max-w-6xl mx-auto dark:bg-gray-800 p-5 rounded-3xl">
                <div className="flex flex-row items-center gap-6 flex-wrap">
                    <div className="relative z-0 flex-1/6 mb-5 group min-w-[8rem]">
                        <input
                            type="checkbox"
                            checked={goal.enabled === true}
                            onChange={(e) => setField("enabled", e.target.checked)}
                            id="checkbox-enabled"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <label htmlFor="checkbox-enabled" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                            Enabled
                        </label>
                    </div>
                    <div className="relative z-0 flex-1/6 mb-5 group min-w-[8rem]">
                        <input
                            type="number"
                            id="floating_progress"
                            className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                            value={goal.progress}
                            onChange={(e) => setField("progress", parseFloat(e.target.value) || 0)}
                            step="0.01"
                            required
                        />
                        <label
                            htmlFor="floating_progress"
                            className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:text-blue-600 peer-focus:dark:text-blue-500"
                        >
                            Progress
                        </label>
                    </div>
                    <div className="relative z-0 flex-1/6 mb-5 group min-w-[8rem]">
                        <input
                            checked={goal.wrapAround === true}
                            onChange={(e) => setField("wrapAround", e.target.checked)}
                            id="checkbox-wrap-around"
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <label htmlFor="checkbox-wrap-around" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                            Wrap Around
                        </label>
                    </div>
                    {(goal.kind === "bits" || goal.kind === "subs") && (
                        <div className="relative z-0 flex-1/6 mb-5 group min-w-[8rem]">
                            <input
                                checked={goal.shared === true}
                                onChange={(e) =>
                                    setField<
                                        NewMultiStepBitsGoalDoc | NewMultiStepSubsGoalDoc,
                                        "shared"
                                    >("shared", e.target.checked)
                                }
                                id="checkbox-shared"
                                type="checkbox"
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label htmlFor="checkbox-shared" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                                Shared
                            </label>
                        </div>
                    )}
                    <div className="relative z-0 flex-1/6 mb-5 group min-w-[8rem]">
                        <input
                            checked={goal.resetOnSessionStart === true}
                            onChange={(e) => setField("resetOnSessionStart", e.target.checked)}
                            id="checkbox-reset-on-session-start"
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <label htmlFor="checkbox-reset-on-session-start" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                            Reset on Session-Start
                        </label>
                    </div>
                </div>

                <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-300 mb-3">Steps (cumulative thresholds)</h3>
                    <div className="flex flex-col gap-4">
                        {goal.steps.map((step, index) => (
                            <div key={index} className="flex flex-row items-end gap-4 flex-wrap">
                                <div className="relative z-0 flex-1 min-w-[12rem] mb-1 group">
                                    <input
                                        type="text"
                                        className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                                        value={step.title}
                                        onChange={(e) => updateStep(index, "title", e.target.value)}
                                        required
                                    />
                                    <label className="absolute text-sm text-gray-500 dark:text-gray-400 -translate-y-6 scale-75">
                                        Step {index + 1} title
                                    </label>
                                </div>
                                <div className="relative z-0 w-32 mb-1 group">
                                    <input
                                        type="number"
                                        className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                                        value={step.goal}
                                        onChange={(e) => updateStep(index, "goal", parseFloat(e.target.value) || 0)}
                                        step="0.01"
                                        required
                                    />
                                    <label className="absolute text-sm text-gray-500 dark:text-gray-400 -translate-y-6 scale-75">
                                        Threshold ({defaultFormatters[goal.kind](step.goal)})
                                    </label>
                                </div>
                                <div className="flex gap-2 mb-2">
                                    <button
                                        type="button"
                                        disabled={index === 0}
                                        className="text-white bg-gray-600 hover:bg-gray-700 disabled:opacity-30 font-medium rounded-lg text-xs px-3 py-2"
                                        onClick={() => moveStep(index, -1)}
                                    >
                                        Up
                                    </button>
                                    <button
                                        type="button"
                                        disabled={index === goal.steps.length - 1}
                                        className="text-white bg-gray-600 hover:bg-gray-700 disabled:opacity-30 font-medium rounded-lg text-xs px-3 py-2"
                                        onClick={() => moveStep(index, 1)}
                                    >
                                        Down
                                    </button>
                                    <button
                                        type="button"
                                        disabled={goal.steps.length <= 1}
                                        className="text-white bg-red-700 hover:bg-red-800 disabled:opacity-30 font-medium rounded-lg text-xs px-3 py-2"
                                        onClick={() => removeStep(index)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="mt-3 text-white bg-gray-600 hover:bg-gray-700 font-medium rounded-lg text-sm px-4 py-2"
                        onClick={addStep}
                    >
                        Add step
                    </button>
                </div>

                <div className="flex flex-row items-center justify-center gap-2 flex-wrap">
                    <button
                        type="button"
                        className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700"
                        onClick={doSave}
                    >
                        Save
                    </button>
                    <button
                        type="button"
                        className="text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-red-600 dark:hover:bg-red-700"
                        onClick={reset}
                    >
                        Reset progress
                    </button>
                    {token && goal._id != null ? (
                        <button
                            type="button"
                            className="flex flex-row gap-2 items-center justify-center text-white bg-blue-700 hover:bg-blue-800 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 dark:bg-blue-600 dark:hover:bg-blue-700"
                            onClick={() =>
                                copyUrl(
                                    `${import.meta.env.VITE_OVERLAY_URL}/obs-widgets/goal/${String(goal._id)}?token=${token}`
                                )
                            }
                        >
                            <FaRegClipboard size={20} /> Copy Browser-Source URL
                        </button>
                    ) : null}
                </div>
            </form>
        </div>
    );
};
